import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AdminAccAuthLoginData,
  type AdminAccAuthLogoutData,
  type AdminAccAuthRefreshData,
  type AdminAccMeGetData,
  type AdminAccMeUpdateData,
  endpoint,
  http,
  parseApiResponse,
  type RefreshIn,
  zAdminAccAuthLoginResponse,
  zAdminAccAuthRefreshResponse,
  zAdminAccMeGetResponse,
  zAdminAccMeUpdateResponse,
} from '@/shared/api';
import type { AuthTokens, LoginRequest, LoginResponse, Me, UpdateMeRequest } from './schemas';

const LOGIN = endpoint<AdminAccAuthLoginData>('/api/v1/admin/auth/login');
const REFRESH = endpoint<AdminAccAuthRefreshData>('/api/v1/admin/auth/refresh');
const LOGOUT = endpoint<AdminAccAuthLogoutData>('/api/v1/admin/auth/logout');
const ME = endpoint<AdminAccMeGetData>('/api/v1/admin/me');
const ME_UPDATE = endpoint<AdminAccMeUpdateData>('/api/v1/admin/me');

/** Вход: 401 invalid_credentials — ошибка формы, а не потеря сессии (skipAuthRefresh). */
export async function login(body: LoginRequest): Promise<LoginResponse> {
  const response = await http.post<unknown>(LOGIN, body, { skipAuthRefresh: true });
  return parseApiResponse(zAdminAccAuthLoginResponse, response.data, LOGIN);
}

/** Обновление пары токенов; refresh-токен ротируется при каждом обновлении (ТЗ COM-01). */
export async function refreshTokens(refreshToken: string): Promise<AuthTokens> {
  const body: RefreshIn = { refresh_token: refreshToken };
  const response = await http.post<unknown>(REFRESH, body, { skipAuthRefresh: true });
  return parseApiResponse(zAdminAccAuthRefreshResponse, response.data, REFRESH).tokens;
}

/** Выход: 204. */
export async function logout(): Promise<void> {
  await http.post(LOGOUT, undefined, { skipAuthRefresh: true });
}

export async function fetchMe(): Promise<Me> {
  const response = await http.get<unknown>(ME);
  return parseApiResponse(zAdminAccMeGetResponse, response.data, ME);
}

/** Язык админки и телефон участника: `{admin_locale?, phone?}` → обновлённый `me`. */
export async function updateMe(body: UpdateMeRequest): Promise<Me> {
  const response = await http.patch<unknown>(ME_UPDATE, body);
  return parseApiResponse(zAdminAccMeUpdateResponse, response.data, ME_UPDATE);
}

export const sessionQueryKeys = {
  me: ['session', 'me'] as const,
};

export function useMe(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: sessionQueryKeys.me,
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
    enabled: options.enabled ?? true,
    meta: { errorToast: false },
  });
}

/**
 * Правка своего профиля участника (`PATCH /admin/me`): ответ — обновлённый `me`, кладётся в кэш.
 * Ошибки (422 — поля) показывает форма, глобальный тост не нужен.
 */
export function useUpdateMe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMe,
    onSuccess: (me) => queryClient.setQueryData(sessionQueryKeys.me, me),
    meta: { errorToast: false },
  });
}
